import { formatInvoiceQuantity, invoiceAddonName, splitInvoiceLines } from './invoiceFormat';

const money = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return '0.00';
  return v.toFixed(2);
};

const fmtDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.slice(0, 10) : d;
  try {
    return new Date(s + 'T12:00:00').toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return String(d);
  }
};

/**
 * Minimal standalone HTML for download / print (matches invoice/receipt preview content).
 */
export function buildInvoiceHtmlDocument(invoice, vendorProfile = {}) {
  const vp = vendorProfile || {};
  const isReceipt = (invoice.document_type || 'invoice') === 'receipt';
  const docLabel = isReceipt ? 'Payment Receipt' : 'Invoice';
  const numberLabel = isReceipt ? 'Receipt #' : 'Invoice #';
  const partyLabel = isReceipt ? 'Received from' : 'Bill to';
  const dateLabel = isReceipt ? 'Date' : 'Invoice date';
  const totalLabel = isReceipt ? 'Total Paid' : 'Total';

  const lines = invoice.line_items?.length
    ? invoice.line_items
    : [
        {
          description: isReceipt ? 'Receipt total' : 'Invoice total',
          quantity: 1,
          unit_price: invoice.subtotal ?? invoice.total_amount,
        },
      ];

  const subtotal = Number(invoice.subtotal ?? 0);
  const discountAmount = Number(invoice.discount_amount ?? 0);
  const discountType = invoice.discount_type || null;
  const discountValue = invoice.discount_value != null ? Number(invoice.discount_value) : null;
  const tax = Number(invoice.tax_amount ?? 0);
  const total = Number(invoice.total_amount ?? 0);
  const taxRate = invoice.tax_rate != null ? Number(invoice.tax_rate) : null;
  const logoUrl = invoice.logo_url || vp.logo_url || vp.brand_settings?.logo_url || null;
  const discountLabel =
    discountType === 'percent' && discountValue > 0
      ? `Discount (${discountValue}%)`
      : 'Discount';

  const client = invoice.client || {};
  const clientName = client.name || invoice.client_name || invoice.guest_name || '';
  const clientEmail = client.email || invoice.guest_email || '';
  const clientPhone = client.phone || invoice.guest_phone || '';

  const vendorLines = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
    vp.business_phone,
  ]
    .filter(Boolean)
    .map((line) => `<div>${escapeHtml(line)}</div>`)
    .join('');

  const logoBlock = logoUrl
    ? `<img src="${String(logoUrl)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}" alt="" style="display:block;max-height:56px;max-width:160px;object-fit:contain;margin:0 0 12px;" />`
    : '';

  const { lines: serviceLines, addons: addonLines } = splitInvoiceLines(lines);
  const moneyRow = (row, addon) => `
    <tr>
      <td style="padding:10px;border-bottom:1px solid #eee;">${escapeHtml(
        addon ? invoiceAddonName(row) : row.description || ''
      )}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">${formatInvoiceQuantity(row.quantity)}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">$${money(row.unit_price)}</td>
      <td style="padding:10px;border-bottom:1px solid #eee;text-align:right;">$${money(
        Number(row.quantity) * Number(row.unit_price)
      )}</td>
    </tr>`;
  const addonHeading = addonLines.length
    ? `<tr><td colspan="4" style="padding:16px 10px 4px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#64748b;">Add-ons</td></tr>`
    : '';
  const rows = [
    ...serviceLines.map((row) => moneyRow(row, false)),
    addonHeading,
    ...addonLines.map((row) => moneyRow(row, true)),
  ].join('');

  const headerBlock = isReceipt
    ? `<div style="text-align:center;border-bottom:2px solid #111;padding-bottom:20px;margin-bottom:24px;">
        ${logoBlock}
        <p style="margin:0;font-size:1.35rem;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;">${docLabel}</p>
        <h1 style="font-size:1.1rem;margin:8px 0 4px;font-weight:500;">${escapeHtml(vp.business_name || 'Receipt')}</h1>
        ${vendorLines}
        <p style="margin-top:12px;"><strong>${numberLabel}</strong> ${escapeHtml(invoice.invoice_number || '')}</p>
      </div>`
    : `${logoBlock}
  <p class="muted">${docLabel}</p>
  <h1>${escapeHtml(vp.business_name || 'Invoice')}</h1>
  ${vendorLines}
  <p style="margin-top:24px;"><strong>${numberLabel}</strong> ${escapeHtml(invoice.invoice_number || '')} &nbsp;·&nbsp; <strong>Status</strong> ${escapeHtml(
      invoice.status || ''
    )}</p>`;

  const dueBlock = isReceipt
    ? ''
    : `<div>Due date: <strong>${fmtDate(invoice.due_date)}</strong></div>`;

  const paymentTermsBlock =
    !isReceipt && invoice.payment_terms
      ? `<p style="margin-top:24px;"><strong>Payment terms</strong><br/>${escapeHtml(invoice.payment_terms)}</p>`
      : '';

  const footerBlock = isReceipt
    ? `<div style="margin-top:40px;text-align:center;font-size:12px;color:#666;">
        <p style="margin:0 0 4px;font-weight:600;color:#333;">Thank you for your business!</p>
        <p style="margin:0;">This is an official receipt for your records.</p>
      </div>`
    : '';

  const discountRow =
    discountAmount > 0
      ? `<div><span>${escapeHtml(discountLabel)}</span><span>−$${money(discountAmount)}</span></div>`
      : '';

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>${escapeHtml(docLabel)} ${escapeHtml(invoice.invoice_number || '')}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, sans-serif; margin: 0; padding: 0; color: #111; background: #fff; }
  .invoice-sheet { max-width: 800px; margin: 0 auto; padding: 40px 48px 48px; }
  h1 { font-size: 1.25rem; margin: 0 0 8px; }
  .muted { color: #666; font-size: 0.875rem; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  th { text-align: left; font-size: 0.75rem; text-transform: uppercase; color: #666; padding: 10px 12px; border-bottom: 2px solid #ddd; }
  th:first-child, td:first-child { padding-left: 0; }
  th:last-child, td:last-child { padding-right: 0; }
  .totals { margin-top: 20px; max-width: 280px; margin-left: auto; padding-right: 4px; }
  .totals div { display: flex; justify-content: space-between; padding: 6px 0; }
  .grand { font-weight: 700; font-size: 1.1rem; border-top: 2px solid #111; padding-top: 10px; margin-top: 8px; }
</style></head><body>
  <div class="invoice-sheet">
  ${headerBlock}
  <div style="display:flex;justify-content:space-between;margin-top:24px;gap:24px;">
    <div>
      <div class="muted" style="margin-bottom:6px;">${partyLabel}</div>
      <div><strong>${escapeHtml(clientName)}</strong></div>
      ${clientEmail ? `<div>${escapeHtml(clientEmail)}</div>` : ''}
      ${clientPhone ? `<div>${escapeHtml(clientPhone)}</div>` : ''}
    </div>
    <div style="text-align:right;">
      <div>${dateLabel}: <strong>${fmtDate(invoice.invoice_date)}</strong></div>
      ${dueBlock}
    </div>
  </div>
  <table>
    <thead><tr>
      <th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="totals">
    <div><span>Subtotal</span><span>$${money(subtotal)}</span></div>
    ${discountRow}
    <div><span>Tax${taxRate != null && taxRate > 0 ? ` (${taxRate}%)` : ''}</span><span>$${money(tax)}</span></div>
    <div class="grand"><span>${totalLabel}</span><span>$${money(total)}</span></div>
  </div>
  ${paymentTermsBlock}
  ${invoice.notes ? `<p style="margin-top:16px;"><strong>Notes</strong><br/>${escapeHtml(invoice.notes)}</p>` : ''}
  ${footerBlock}
  </div>
</body></html>`;
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
